import { Fragment } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { selectAuth } from "../redux/features/auth/auth-slice";
import { selectProfile } from "../redux/features/user/user-slice";
import DashboardLayout from "../shared/components/dashboard-layout/dashboard-layout";
import PublicRoute from "../shared/components/public-route/public-route";
import { UserRole } from "../shared/oversight-core/enums/user-roles";
import { AppRoute } from "../shared/oversight-core/interfaces/app-routes";
import IPageInfo from "../shared/oversight-core/interfaces/page-info";
import NotFound from "../shared/oversight-core/shared-components/not-found/not-found";
import PrivateRoute from "../shared/oversight-core/shared-components/private-route/private-route";
import Unauthorized from "../shared/oversight-core/shared-components/unauthorized/unauthorized";
import ChangeEmail from "../shared/oversight-core/shared-pages/change-email/change-email";
import ForgotPassword from "../shared/oversight-core/shared-pages/forgot-password/forgot-password";
import ResetPassword from "../shared/oversight-core/shared-pages/reset-password/reset-password";
import ProfileDetails from "../shared/oversight-core/shared-pages/user-profile/profile-details/profile-details";
import ThemeDetails from "../shared/oversight-core/shared-pages/user-profile/theme-details/theme-details";
import VerifyOtp from "../shared/oversight-core/shared-pages/verify-otp/verify-otp";
import RemoveTrailingSlash from "../shared/oversight-core/utils/removeTrailingSlash";
import Devices from "./dashboard/devices/devices";
import ViewDevice from "./dashboard/devices/view-device/view-device";
import DLB from "./dashboard/dlb/dlb";
import History from "./dashboard/history/history";
import Home from "./dashboard/home/home";
import Users from "./dashboard/users/users";
import Login from "./login/login";
import Register from "./register/register";

/** Roles allowed to access standard consumer dashboard routes.
 * ROLE_SOLAR_DASHBOARD_ADMIN is intentionally unsupported — it was removed from
 * UserRole in oversight-core; admin capabilities are covered by ROLE_ADMIN. */
const ALLOWED_ROLES: UserRole[] = [UserRole.ROLE_USER, UserRole.ROLE_ADMIN];

export const publicRoutes: IPageInfo[] = [
  {
    name: "Login",
    path: AppRoute.LOGIN,
    component: <Login />,
    enableRoute: true,
  },
  {
    name: "Register",
    path: AppRoute.REGISTER,
    component: <Register />,
    enableRoute: true,
  },
  {
    name: "Forgot Password",
    path: AppRoute.FORGOT_PASSWORD,
    component: <ForgotPassword />,
    enableRoute: true,
  },
  {
    name: "Reset Password",
    path: AppRoute.RESET_PASSWORD,
    component: <ResetPassword />,
    enableRoute: true,
  },
  {
    name: "Verify Email",
    path: AppRoute.VERIFY_EMAIL,
    component: <VerifyOtp />,
    enableRoute: true,
  },
];

export const getConsumerPrivateRoutes = (userRole?: UserRole): IPageInfo[] => {
  const allSubRoutes: IPageInfo[] = [
    {
      name: "Home",
      icon: "home",
      path: AppRoute.HOME,
      index: true,
      component: <Home />,
      enableRoute: true,
      bgColor: "bg-primary",
      bgColorIcon: "--sidebar-menu-item",
      showOnDrawer: true,
      roles: ALLOWED_ROLES,
    },
    {
      name: "Devices",
      path: AppRoute.DEVICES,
      icon: "ev_station",
      component: <Devices />,
      enableRoute: true,
      bgColor: "bg-primary",
      bgColorIcon: "--sidebar-menu-item",
      showOnDrawer: true,
      roles: ALLOWED_ROLES,
    },
    {
      name: "Device",
      path: AppRoute.DEVICES + "/:id",
      icon: "devices_other",
      component: <ViewDevice />,
      enableRoute: true,
      bgColor: "bg-primary",
      bgColorIcon: "--sidebar-menu-item",
      showOnDrawer: false,
      roles: ALLOWED_ROLES,
    },
    {
      name: "History",
      icon: "pie_chart",
      path: AppRoute.HISTORY,
      index: true,
      component: <History />,
      enableRoute: true,
      bgColor: "bg-primary",
      bgColorIcon: "--sidebar-menu-item",
      showOnDrawer: true,
      roles: ALLOWED_ROLES,
    },
    {
      name: "DLB",
      icon: "display_settings",
      path: AppRoute.DLB,
      index: true,
      component: <DLB />,
      enableRoute: true,
      bgColor: "bg-primary",
      bgColorIcon: "--sidebar-menu-item",
      showOnDrawer: true,
      roles: ALLOWED_ROLES,
    },
    {
      name: "Users",
      icon: "person",
      path: AppRoute.USERS,
      index: true,
      component: <Users />,
      enableRoute: true,
      bgColor: "bg-primary",
      bgColorIcon: "--sidebar-menu-item",
      showOnDrawer: true,
      roles: [UserRole.ROLE_ADMIN],
    },
    {
      name: "Profile Details",
      path: AppRoute.PROFILE_DETAILS,
      component: <ProfileDetails />,
      enableRoute: true,
      showOnDrawer: false,
      roles: ALLOWED_ROLES,
    },
    {
      name: "Theme",
      path: AppRoute.THEME,
      component: <ThemeDetails />,
      enableRoute: true,
      showOnDrawer: false,
      roles: ALLOWED_ROLES,
    },
    {
      name: "404 Page Not Found",
      path: AppRoute.NOT_FOUND,
      component: <NotFound />,
      showOnDrawer: false,
    },
  ];

  const subRoutes = allSubRoutes.filter((route) => {
    if (!route.roles || route.roles.length === 0) return true;
    if (!userRole) return false;
    return route.roles.includes(userRole);
  });

  return [
    {
      name: "Email Verification",
      path: AppRoute.USER_EMAIL_VERIFY,
      component: <VerifyOtp />,
      enableRoute: true,
    },
    {
      name: "Change Email",
      path: AppRoute.CHANGE_EMAIL,
      component: <ChangeEmail />,
      showOnDrawer: false,
    },
    {
      name: "Change Password",
      path: AppRoute.CHANGE_PASSWORD,
      component: <ResetPassword />,
      enableRoute: true,
    },
    {
      name: "Dashboard",
      path: AppRoute.DASHBOARD,
      component: <DashboardLayout />,
      root: true,
      subRoutes,
    },
  ];
};

const AppRoutes = () => {
  const auth = useSelector(selectAuth);
  const profile = useSelector(selectProfile);

  const getRoutes = (routes: IPageInfo[]) => {
    return routes.map((privateRoutes: IPageInfo, index) => (
      <Route
        key={index}
        path={privateRoutes.path}
        element={privateRoutes.component}
      >
        {privateRoutes.subRoutes?.map((subRoute: IPageInfo, index) => {
          const isAllowed =
            (!subRoute.roles ||
              (profile?.role && subRoute.roles.includes(profile.role))) &&
            (!subRoute.privilege ||
              auth.privileges?.includes(subRoute.privilege));

          const route = [
            <Route
              key={index}
              path={subRoute.path}
              element={isAllowed ? subRoute.component : <Unauthorized />}
            />,
          ];

          if (subRoute.index) {
            route.push(
              <Route
                key={index + subRoute.path}
                index
                element={<Navigate to={subRoute.path} />}
              />,
            );
          }
          return route;
        })}
      </Route>
    ));
  };

  return (
    <Fragment>
      <RemoveTrailingSlash />
      <Routes>
        <Route path="/" element={<PublicRoute />}>
          <Route index element={<Navigate to={AppRoute.LOGIN} />} />
          {publicRoutes.map((p: IPageInfo, index) => {
            return <Route key={index} path={p.path} element={p.component} />;
          })}
          <Route path="*" element={<Navigate to={AppRoute.LOGIN} />} />
        </Route>

        <Route path="/" element={<PrivateRoute role={ALLOWED_ROLES} />}>
          {getRoutes(getConsumerPrivateRoutes(profile?.role))}
        </Route>

        <Route path="/unauthorized" element={<Unauthorized />} />
      </Routes>
    </Fragment>
  );
};

export default AppRoutes;
